if (!customElements.get('wlsn-header-search')) {
  class WLSNHeaderSearch extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        lockScroll: 'wlsn-lock-scroll',
        headerSearchToggle: 'js-wlsn-header-search-toggle',
        headerSearchCloseBtn: 'wlsn-search-form__input-clear-btn',
        headerSearch: 'wlsn-header-search',
        headerSearchOverlay: 'js-wlsn-header-search-overlay',
        headerSearchModal: 'js-wlsn-header-search-modal',
      };

      this.isHeaderSearchOpen = false;
      /** @type {ReturnType<typeof setTimeout> | null} */
      this._outsideClickListenTimeoutId = null;
      /** @type {(() => void) | null} */
      this._boundSyncBottomOffset = null;
      /** @type {ResizeObserver | null} */
      this._hostBottomResizeObserver = null;
      /** Subtracted from `getBoundingClientRect().bottom` (px), e.g. header border. */
      this.borderOffset = 0;

      this.bindMethods = {
        toggleHeaderSearch: this._toggleHeaderSearch.bind(this),
        closeHeaderSearch: this._closeHeaderSearch.bind(this),
        onHeaderSearchOverlayClick: this._onHeaderSearchOverlayClick.bind(this),
        onEscKey: this._onEscKey.bind(this),
        onDocumentClickOutside: this._onDocumentClickOutside.bind(this),
      };
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        headerSearchToggles: document.querySelectorAll(`.${this.classes.headerSearchToggle}`),
        headerSearchCloseBtn: this.querySelector(`.${this.classes.headerSearchCloseBtn}`),
        headerSearch: this,
        headerSearchOverlay: this.querySelector(`.${this.classes.headerSearchOverlay}`),
        headerSearchModal: this.querySelector(`.${this.classes.headerSearchModal}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      const { headerSearchToggles, headerSearchCloseBtn, headerSearchOverlay } = this.elements;
      // Header search
      if (headerSearchToggles.length > 0) {
        headerSearchToggles.forEach((toggle) => {
          toggle.addEventListener('click', this.bindMethods.toggleHeaderSearch);
        });
      }

      if (headerSearchCloseBtn) {
        headerSearchCloseBtn.addEventListener('click', this.bindMethods.closeHeaderSearch);
      }

      if (headerSearchOverlay) {
        headerSearchOverlay.addEventListener('click', this.bindMethods.onHeaderSearchOverlayClick);
      }

      // ESC key handle for all modals
      document.addEventListener('keydown', this.bindMethods.onEscKey);

      this._boundSyncBottomOffset = () => this._syncBottomOffsetFromHost();
      window.addEventListener('resize', this._boundSyncBottomOffset);
      window.addEventListener('scroll', this._boundSyncBottomOffset, { passive: true });
      this._hostBottomResizeObserver = new ResizeObserver(() => this._syncBottomOffsetFromHost());
      this._hostBottomResizeObserver.observe(this);
      this._scheduleSyncBottomOffset();
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      const { headerSearchToggles, headerSearchCloseBtn, headerSearchOverlay } = this.elements;
      if (headerSearchToggles.length > 0) {
        headerSearchToggles.forEach((toggle) => {
          toggle.removeEventListener('click', this.bindMethods.toggleHeaderSearch);
        });
      }

      if (headerSearchCloseBtn) {
        headerSearchCloseBtn.removeEventListener('click', this.bindMethods.closeHeaderSearch);
      }

      if (headerSearchOverlay) {
        headerSearchOverlay.removeEventListener('click', this.bindMethods.onHeaderSearchOverlayClick);
      }

      document.removeEventListener('keydown', this.bindMethods.onEscKey);
      this._teardownOutsideClickClose();
      if (this._boundSyncBottomOffset) {
        window.removeEventListener('resize', this._boundSyncBottomOffset);
        window.removeEventListener('scroll', this._boundSyncBottomOffset, { passive: true });
        this._boundSyncBottomOffset = null;
      }
      if (this._hostBottomResizeObserver) {
        this._hostBottomResizeObserver.disconnect();
        this._hostBottomResizeObserver = null;
      }
    }

    /**
     * Toggle header search open/closed
     * @param {HTMLElement} trigger - The trigger element
     * @returns {void}
     */
    _toggleHeaderSearch(trigger) {
      const { headerSearch } = this.elements;
      if (!trigger) return;
      this._lockScroll();
      if (this.isHeaderSearchOpen) {
        this._closeHeaderSearch();
      } else {
        this.isHeaderSearchOpen = true;
        headerSearch?.classList.add(this.classes.active);
        this._updateOverlayState();
        this._focusSearchInput();
        this._scheduleOutsideClickClose();

        // 200ms delay because transition for transform is 0.2s in css
        setTimeout(() => {
          this._scheduleSyncBottomOffset();
        }, 200);
      }
    }

    /**
     * Focus the search field after open (next frame so layout / drawer is ready).
     * @returns {void}
     */
    _focusSearchInput() {
      const input = this.querySelector('input[type="search"]');
      if (!input) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          input.focus({ preventScroll: true });
        });
      });
    }

    /**
     * Bottom edge of this host from the top of the viewport (px), minus {@link borderOffset};
     * exposed as `--wlsn-bottom-offset` in rem (same pattern as header bottom position vars).
     * @returns {void}
     */
    _syncBottomOffsetFromHost() {
      const { headerSearchModal } = this.elements;
      if (!headerSearchModal) return;
      const rootFontPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const bottomPx = parseInt(headerSearchModal.getBoundingClientRect().bottom - this.borderOffset, 10);
      const offsetRem = bottomPx / rootFontPx;
      this.style.setProperty('--wlsn-header-search-bottom-offset', `${offsetRem}rem`);
    }

    /**
     * @returns {void}
     */
    _scheduleSyncBottomOffset() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._syncBottomOffsetFromHost();
        });
      });
    }

    /**
     * Close header search
     * @returns {void}
     */
    _closeHeaderSearch() {
      const { headerSearch } = this.elements;
      if (!this.isHeaderSearchOpen) return;
      this._teardownOutsideClickClose();
      this.isHeaderSearchOpen = false;
      headerSearch?.classList.remove(this.classes.active);
      this._updateOverlayState();
      this._unlockScroll();
    }

    /**
     * After the opening click finishes bubbling, listen for clicks outside the host.
     * @returns {void}
     */
    _scheduleOutsideClickClose() {
      this._teardownOutsideClickClose();
      this._outsideClickListenTimeoutId = setTimeout(() => {
        this._outsideClickListenTimeoutId = null;
        if (!this.isHeaderSearchOpen) return;
        document.addEventListener('click', this.bindMethods.onDocumentClickOutside);
      }, 0);
    }

    /**
     * @returns {void}
     */
    _teardownOutsideClickClose() {
      if (this._outsideClickListenTimeoutId != null) {
        clearTimeout(this._outsideClickListenTimeoutId);
        this._outsideClickListenTimeoutId = null;
      }
      document.removeEventListener('click', this.bindMethods.onDocumentClickOutside);
    }

    /**
     * @param {MouseEvent} e
     * @returns {void}
     */
    _onDocumentClickOutside(e) {
      if (!this.isHeaderSearchOpen) return;
      if (this.contains(e.target)) return;
      if (e.target instanceof Element && e.target.closest(`.${this.classes.headerSearchToggle}`)) {
        return;
      }
      this._closeHeaderSearch();
    }

    /**
     * Handle header search toggle click event
     * @param {MouseEvent} e - The mouse event
     * @returns {void}
     */
    _onHeaderSearchOverlayClick(e) {
      if (this.isHeaderSearchOpen) {
        this._closeHeaderSearch();
      }
    }

    /**
     * Update overlay state based on header search state
     * @returns {void}
     */
    _updateOverlayState() {
      const { headerSearchOverlay } = this.elements;
      headerSearchOverlay?.classList.toggle(this.classes.active, this.isHeaderSearchOpen);
    }

    /**
     * Handle ESC key press
     * @param {KeyboardEvent} e - The keyboard event
     * @returns {void}
     */
    _onEscKey(e) {
      if (e.key === 'Escape') {
        if (this.isHeaderSearchOpen) {
          this._closeHeaderSearch();
          return;
        }
      }
    }

    /**
     * Lock body scroll
     * @returns {void}
     */
    _lockScroll() {
      document.body.classList.add(this.classes.lockScroll);
    }

    /**
     * Unlock body scroll
     * @returns {void}
     */
    _unlockScroll() {
      document.body.classList.remove(this.classes.lockScroll);
    }
  }

  customElements.define('wlsn-header-search', WLSNHeaderSearch);
}
